<template>
  <figure>
    <PlayButton :playing="playing">
      <button v-if="!playing" class="play" @click="start" />
      <button v-if="playing" class="pause" @click="pause" />
    </PlayButton>
    <header>
      <h5>{{ item.composer }}:</h5>
      <Marquee :playing="playing">
        <h3 v-html="item.title" />
        <h4 v-html="item.artists" />
      </Marquee>
    </header>
  </figure>
</template>

<script setup>
import { ref, onUnmounted, watch } from 'vue';
import { Howl, Howler } from 'howler';
import { crossOriginSrc } from '@/utils';
import { composables } from '@/composables';

const props = defineProps(['file', 'item', 'isFirst']);
const playing = ref(false);
const soundDataStore = composables().soundDataStore;

let sound;
let analyser;
let animationFrameId;
let visualizationSetup = false;
let dataArray;
let frameCount = 0;

// Preload ONLY the first track when file becomes available
watch(() => props.file, (file) => {
  if (props.isFirst && file?.url && !sound) {
    const audioUrl = crossOriginSrc(file.url);
    if (audioUrl && audioUrl.match(/\.(mp3|wav|ogg|m4a|aac|flac)$/i)) {
      sound = new Howl({
        src: [audioUrl],
        html5: true,
        preload: true,
        onplay: () => {
          if (Howler.ctx.state === 'suspended') {
            Howler.ctx.resume();
          }
          setupVisualization();
          startAnimationLoop();
        },
      });
    }
  }
}, { immediate: true });

const averageArrayValuesToGroups = (array, groupSize) =>
  Array.from(
    { length: Math.ceil(array.length / groupSize) },
    (_, i) =>
      array
        .slice(i * groupSize, i * groupSize + groupSize)
        .reduce((sum, num) => sum + num, 0) / groupSize
  );

const setupVisualization = () => {
  // Only set up audio nodes once (prevents MediaElementSourceNode error)
  if (visualizationSetup) return;

  const audioContext = Howler.ctx;
  const audioEl = sound._sounds[0]._node; // HTMLAudioElement

  if (!audioEl) return;

  try {
    // Create MediaElementSourceNode for analyser
    const sourceNode = audioContext.createMediaElementSource(audioEl);

    analyser = audioContext.createAnalyser();
    analyser.fftSize = 256; // Increased from 256 for better stability
    analyser.smoothingTimeConstant = 0.8; // Add smoothing to reduce glitches

    sourceNode.connect(analyser);
    analyser.connect(audioContext.destination); // Ensure audio still plays

    const bufferLength = analyser.frequencyBinCount;
    dataArray = new Uint8Array(bufferLength);

    visualizationSetup = true;
  } catch (error) {
    console.error('Failed to set up audio visualization:', error);
  }
};

const startAnimationLoop = () => {
  // Can be called multiple times (on play/resume)
  if (!analyser || !dataArray) return;

  const update = () => {
    if (!playing.value) return; // Stop updating when paused

    frameCount++;
    if (frameCount % 2 === 0) { // Update every other frame
      analyser.getByteFrequencyData(dataArray);

      // Boost visualization: apply gain and dynamic range compression
      const boostedData = dataArray.map(value => {
        // Normalize to 0-1 range
        const normalized = value / 255;
        // Apply power curve to boost quiet parts (exponent < 1 boosts lows)
        const boosted = Math.pow(normalized, 0.6);
        // Apply gain multiplier
        const amplified = Math.min(boosted * 2.5, 1);
        // Convert back to 0-255 range
        return amplified * 255;
      });

      soundDataStore.value = averageArrayValuesToGroups(boostedData, 1);
    }

    animationFrameId = requestAnimationFrame(update);
  };
  update();
};

const start = async () => {
  if (!sound) {
    const audioUrl = crossOriginSrc(props.file?.url);

    // Check if we have a valid URL with an extension
    if (!audioUrl || !audioUrl.match(/\.(mp3|wav|ogg|m4a|aac|flac)$/i)) {
      return;
    }

    // Stop all other sounds before creating a new one
    Howler.stop();

    sound = new Howl({
      src: [audioUrl],
      html5: true, // IMPORTANT for mobile sound playback
      onplay: () => {
        // Resume AudioContext on user interaction (mobile fix)
        if (Howler.ctx.state === 'suspended') {
          Howler.ctx.resume();
        }
        setupVisualization();
        startAnimationLoop();
      },
    });
  }

  sound.play();
  playing.value = true;
};

const pause = () => {
  if (sound) sound.pause();
  playing.value = false;
  // Clear sound data so particles return to normal
  soundDataStore.value = new Uint8Array(256);
  // Animation loop will stop due to playing.value check
};

onUnmounted(() => {
  cancelAnimationFrame(animationFrameId);
  if (sound) {
    sound.unload();
    sound = null;
  }
  visualizationSetup = false;
});
</script>

<style scoped>
figure {
  width: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) 90%, transparent 99%);
}
header {
  display: flex;
  align-items: center;
}
h3 {
  position: relative;
  top: -0.4rem;
  font-size: 2.5rem;
  font-style: italic;
}
h4 {
  text-transform: uppercase;
}
h5,
header :deep(h4),
header :deep(h3) {
  color: var(--gray);
  white-space: nowrap;
  margin: 0;
  margin-right: 1rem;
}
</style>
